<template>
  <lila-default-template>
    <template #mainContent>
      <div class="container production-page">
        <div class="sp-team">
          <div>
            <h1 class="sp-team-name">
              Produção SUS
            </h1>
            <p class="sp-team-sub">
              {{ team.name }} · {{ team.teamLabel }} · CNES {{ team.cnes || "não informado" }}
            </p>
          </div>
        </div>

        <lila-message :alert-data="alert" />

        <p class="sp-intro">
          O que a equipe registra na Lila vira produção sugerida, já com o código SIGTAP.
          A equipe confere, confirma e exporta o arquivo do BPA para a secretaria de saúde.
        </p>

        <section class="sp-section">
          <div class="sp-tiles">
            <div class="sp-card sp-tile">
              <span class="sp-tile-value">{{ counts.a_confirmar }}</span>
              <span class="sp-tile-label">a confirmar</span>
              <p class="sp-tile-note">
                Gerados pelo uso da Lila na competência {{ competence.label }}.
              </p>
            </div>
            <div
              class="sp-card sp-tile"
              :class="{ 'is-critical': counts.pendencia > 0 }"
            >
              <span class="sp-tile-value">{{ counts.pendencia }}</span>
              <span class="sp-tile-label">com pendência</span>
              <p class="sp-tile-note">
                Seriam glosados no SIA. Corrija antes de exportar.
              </p>
            </div>
            <div class="sp-card sp-tile">
              <span class="sp-tile-value">{{ counts.confirmado }}</span>
              <span class="sp-tile-label">prontos para o BPA</span>
              <p class="sp-tile-note">
                {{ counts.exportado }} já {{ counts.exportado === 1 ? "exportado" : "exportados" }} nesta competência.
              </p>
            </div>
            <div
              class="sp-card sp-tile"
              :class="goalReached ? 'is-good' : 'is-warning'"
            >
              <span class="sp-tile-value">
                {{ goalTotal }} <span class="sp-tile-of">de {{ goal }}</span>
              </span>
              <span class="sp-tile-label">na meta da PNCP</span>
              <p class="sp-tile-note">
                Confirmados e exportados nesta competência.
                A competência fecha {{ closingText }}.
              </p>
            </div>
          </div>
        </section>

        <section class="sp-section">
          <div class="sp-section-header">
            <h2 class="sp-section-title">
              Procedimentos gerados pela Lila
            </h2>
            <div class="sp-header-actions">
              <button
                type="button"
                class="sp-button is-ghost"
                @click="activityModalOpen = true"
              >
                Registrar atividade educativa
              </button>
              <button
                type="button"
                class="sp-button is-primary"
                :disabled="confirmableIds.length === 0"
                @click="confirmAll"
              >
                Confirmar {{ confirmableIds.length }} sem pendência
              </button>
            </div>
          </div>

          <educational-activity-modal
            v-model:open="activityModalOpen"
            :default-professional-name="professionalName()"
            :default-cbo="loggedProfessionalCbo"
            @save="registerActivity"
          />

          <div
            class="sp-filters"
            role="tablist"
            aria-label="Filtrar procedimentos"
          >
            <button
              v-for="option in filters"
              :key="option.key"
              type="button"
              role="tab"
              class="sp-filter"
              :class="{ 'is-active': filter === option.key }"
              :aria-selected="filter === option.key"
              @click="filter = option.key"
            >
              {{ option.label }}
              <span class="sp-filter-count">{{ option.key === "todos" ? rows.length : counts[option.key] }}</span>
            </button>
          </div>

          <div class="sp-card sp-table-card">
            <p
              v-if="visibleRows.length === 0"
              class="sp-empty"
            >
              Nenhum procedimento nesta situação.
            </p>
            <table
              v-else
              class="sp-table"
            >
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Procedimento</th>
                  <th>Paciente</th>
                  <th>Profissional</th>
                  <th>Origem na Lila</th>
                  <th>Situação</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in visibleRows"
                  :key="row.id"
                  :class="{ 'is-discarded': row.status === 'descartado' }"
                >
                  <td class="is-nowrap">
                    {{ formatDate(row.date) }}
                  </td>
                  <td>
                    <span class="sp-code">{{ formatProcedureCode(row.code) }}</span>
                    <span class="sp-muted">{{ row.procedureName || (procedures[row.code] || {}).name }}</span>
                    <span class="sp-instrument">{{ row.instrument || (procedures[row.code] || {}).instrument }}</span>
                  </td>
                  <td>
                    <template v-if="row.patient">
                      <span class="sp-strong">{{ row.patient.name }}</span>
                      <span class="sp-muted">
                        CNS {{ row.loading ? "verificando…" : maskCns(row.patient.cns) }}
                        <template v-if="row.patient.cid"> · CID {{ row.patient.cid }}</template>
                      </span>
                    </template>
                    <template v-else>
                      <span class="sp-strong">Consolidado</span>
                      <span class="sp-muted">Quantidade: {{ row.quantity }}</span>
                    </template>
                  </td>
                  <td>
                    <span class="sp-strong">{{ row.professional.name }}</span>
                    <span
                      v-if="row.professional.cbo"
                      class="sp-muted"
                    >CBO {{ row.professional.cbo }} · {{ cboLabels[row.professional.cbo] || "não identificado" }}</span>
                    <span
                      v-else
                      class="sp-muted"
                    >CBO não informado</span>
                  </td>
                  <td>
                    <span>{{ row.origin }}</span>
                  </td>
                  <td>
                    <span
                      class="sp-status"
                      :class="`is-${statusTone(row.status)}`"
                    >
                      <span aria-hidden="true">{{ statusIcon(row.status) }}</span>
                      {{ row.loading ? "Verificando" : statusLabels[row.status] }}
                    </span>
                    <ul
                      v-if="row.status === 'pendencia'"
                      class="sp-issues"
                    >
                      <li
                        v-for="issue in row.issues"
                        :key="issue"
                      >
                        {{ issue }}
                      </li>
                    </ul>
                    <span
                      v-if="row.status === 'exportado' && row.exportFile"
                      class="sp-muted"
                    >{{ row.exportFile }}</span>
                  </td>
                  <td class="sp-actions">
                    <template v-if="!row.loading">
                      <button
                        v-if="row.status === 'a_confirmar'"
                        type="button"
                        class="sp-button is-small is-primary"
                        @click="confirm(row)"
                      >
                        Confirmar
                      </button>
                      <button
                        v-if="row.status === 'a_confirmar' || row.status === 'pendencia'"
                        type="button"
                        class="sp-button is-small is-ghost"
                        @click="discard(row)"
                      >
                        Descartar
                      </button>
                      <router-link
                        v-if="row.status === 'pendencia' && row.patientId"
                        :to="{ name: 'myPatientMedicalRecord', params: { id: row.patientId } }"
                        class="sp-button is-small is-ghost"
                      >
                        Corrigir na Ficha
                      </router-link>
                      <button
                        v-if="row.status === 'confirmado' || row.status === 'descartado'"
                        type="button"
                        class="sp-button is-small is-ghost"
                        @click="undo(row)"
                      >
                        Desfazer
                      </button>
                      <button
                        v-if="row.manual && row.status !== 'exportado'"
                        type="button"
                        class="sp-button is-small is-ghost"
                        @click="removeActivity(row)"
                      >
                        Excluir
                      </button>
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section class="sp-section">
          <h2 class="sp-section-title">
            Exportar BPA
          </h2>

          <div class="sp-card sp-export">
            <div class="sp-export-main">
              <p class="sp-export-headline">
                <strong>{{ exportPreview.individualCount }}</strong> {{ exportPreview.individualCount === 1 ? "linha" : "linhas" }} de BPA-I e
                <strong>{{ exportPreview.consolidatedCount }}</strong> {{ exportPreview.consolidatedCount === 1 ? "linha" : "linhas" }} de BPA-C
                prontas para a competência {{ competence.label }}.
              </p>
              <p class="sp-muted">
                O arquivo segue a estrutura do BPA Magnético (cabeçalho e linhas 02 e 03) e vai para a secretaria
                de saúde, que processa no SIA. Layout de demonstração: ainda não validado contra a versão vigente do DATASUS.
              </p>
            </div>
            <div class="sp-export-actions">
              <button
                type="button"
                class="sp-button is-ghost"
                :disabled="confirmedRows.length === 0"
                @click="showPreview = !showPreview"
              >
                {{ showPreview ? "Ocultar arquivo" : "Pré-visualizar arquivo" }}
              </button>
              <button
                type="button"
                class="sp-button is-primary"
                :disabled="confirmedRows.length === 0"
                @click="exportBpa"
              >
                Exportar arquivo
              </button>
            </div>
          </div>

          <pre
            v-if="showPreview && confirmedRows.length > 0"
            class="sp-preview"
          >{{ exportPreview.content }}</pre>

          <div
            v-if="exports.length > 0"
            class="sp-card sp-history"
          >
            <h3 class="sp-history-title">
              Arquivos exportados
            </h3>
            <ul>
              <li
                v-for="record in exports"
                :key="record.id"
              >
                <span class="sp-strong">{{ record.fileName }}</span>
                <span class="sp-muted">
                  {{ formatDateTime(record.at) }} por {{ record.by }} · {{ record.itemCount }}
                  {{ record.itemCount === 1 ? "procedimento" : "procedimentos" }}
                </span>
              </li>
            </ul>
          </div>
        </section>
      </div>
    </template>
  </lila-default-template>
</template>

<script>
import moment from "moment";
import DefaultTemplate from "@components/template/DefaultTemplate.vue";
import Message from "@components/shared/message/Message.vue";
import EducationalActivityModal from "@components/management/EducationalActivityModal.vue";
import { getFirstCbo } from "@/services/palliative/professionalProfileApi";
import {
  CBO_LABELS,
  ITEM_STATUS,
  PNCP_MINIMUM_PER_MONTH,
  PROCEDURES,
  addEducationalActivity,
  confirmItems,
  currentCompetence,
  discardItems,
  formatProcedureCode,
  getSusProduction,
  registerExport,
  removeEducationalActivity,
  undoItems
} from "@/services/palliative/susProductionApi";
import { showApiError } from "@/services/palliative/http";
import { buildBpaFile, downloadTextFile } from "@/services/palliative/bpaExport";
import { pocLiveSync } from "@/services/palliative/pocLiveSync";

export default {
  name: "SusProduction",
  components: {
    "lila-default-template": DefaultTemplate,
    "lila-message": Message,
    "educational-activity-modal": EducationalActivityModal
  },
  // Itens gerados pelo uso da Lila (por qualquer profissional) aparecem sem recarregar a página.
  mixins: [pocLiveSync("reload")],
  data() {
    return {
      team: { name: "", teamLabel: "", cnes: "" },
      competence: currentCompetence(),
      procedures: PROCEDURES,
      cboLabels: CBO_LABELS,
      goal: PNCP_MINIMUM_PER_MONTH,
      goalTotal: 0,
      rows: [],
      exports: [],
      busy: false,
      loggedProfessionalCbo: "",
      filter: "todos",
      showPreview: false,
      activityModalOpen: false,
      alert: {
        message: "",
        type: "",
        show: false
      },
      statusLabels: {
        [ITEM_STATUS.pending]: "A confirmar",
        [ITEM_STATUS.issue]: "Pendência",
        [ITEM_STATUS.confirmed]: "Confirmado",
        [ITEM_STATUS.discarded]: "Descartado",
        [ITEM_STATUS.exported]: "Exportado"
      },
      filters: [
        { key: "todos", label: "Todos" },
        { key: ITEM_STATUS.pending, label: "A confirmar" },
        { key: ITEM_STATUS.issue, label: "Pendências" },
        { key: ITEM_STATUS.confirmed, label: "Confirmados" },
        { key: ITEM_STATUS.exported, label: "Exportados" },
        { key: ITEM_STATUS.discarded, label: "Descartados" }
      ]
    };
  },
  computed: {
    sortedRows() {
      return [...this.rows].sort((a, b) => String(b.date).localeCompare(String(a.date)));
    },
    visibleRows() {
      if (this.filter === "todos") return this.sortedRows;
      return this.sortedRows.filter((row) => row.status === this.filter);
    },
    counts() {
      const counts = Object.values(ITEM_STATUS).reduce((acc, status) => ({ ...acc, [status]: 0 }), {});
      this.rows.forEach((row) => {
        counts[row.status] += 1;
      });
      return counts;
    },
    confirmableIds() {
      return this.rows
        .filter((row) => row.status === ITEM_STATUS.pending)
        .map((row) => row.id);
    },
    confirmedRows() {
      return this.rows.filter((row) => row.status === ITEM_STATUS.confirmed);
    },
    goalReached() {
      return this.goalTotal >= this.goal;
    },
    closingText() {
      const days = this.competence.daysToClose;
      if (days === 0) return "hoje";
      return days === 1 ? "amanhã" : `em ${days} dias`;
    },
    exportPreview() {
      return buildBpaFile(this.team, this.competence.code, this.confirmedRows);
    }
  },
  async created() {
    await this.reload();
    // CBO sugerido no registro de atividade: o primeiro informado no perfil de CP do profissional logado.
    const session = this.$Session.get(this.$userSessionName);
    const id = session && session.professional && session.professional.Id;
    if (id) this.loggedProfessionalCbo = await getFirstCbo(id);
  },
  methods: {
    async reload() {
      try {
        const data = await getSusProduction(this.competence.code);
        this.team = data.team;
        this.competence = data.competence;
        this.goal = data.goal;
        this.goalTotal = data.goalTotal;
        this.rows = data.rows;
        this.exports = data.exports;
      } catch (error) {
        showApiError(this, this.alert, error, "Não foi possível carregar a Produção SUS.");
      }
    },
    // Executa uma ação na API e recarrega a lista.
    async run(action, successMessage) {
      if (this.busy) return false;
      this.busy = true;
      try {
        await action();
        if (successMessage) this.$Utilities.controlAlert(null, this.alert, "success", successMessage);
        return true;
      } catch (error) {
        showApiError(this, this.alert, error);
        return false;
      } finally {
        this.busy = false;
        await this.reload();
      }
    },
    registerActivity(activity) {
      this.run(() => addEducationalActivity(activity), "Atividade educativa registrada.");
    },
    removeActivity(row) {
      this.run(() => removeEducationalActivity(row.activityId));
    },
    professionalName() {
      const session = this.$Session.get(this.$userSessionName);
      return (session && session.professional && session.professional.Name) || "Profissional";
    },
    confirm(row) {
      this.run(() => confirmItems([row.id]));
    },
    confirmAll() {
      if (this.confirmableIds.length === 0) return;
      this.run(() => confirmItems(this.confirmableIds));
    },
    discard(row) {
      this.run(() => discardItems([row.id]));
    },
    undo(row) {
      this.run(() => undoItems([row.id]));
    },
    // O arquivo é gerado aqui; a API guarda uma cópia e marca os itens como exportados.
    async exportBpa() {
      const file = this.exportPreview;
      const saved = await this.run(() => registerExport({
        competence: this.competence.code,
        fileName: file.fileName,
        content: file.content,
        itemIds: this.confirmedRows.map((row) => row.id)
      }));
      if (!saved) return;
      downloadTextFile(file.fileName, file.content);
      this.showPreview = false;
    },
    statusTone(status) {
      return {
        [ITEM_STATUS.pending]: "info",
        [ITEM_STATUS.issue]: "critical",
        [ITEM_STATUS.confirmed]: "good",
        [ITEM_STATUS.exported]: "done",
        [ITEM_STATUS.discarded]: "muted"
      }[status];
    },
    statusIcon(status) {
      return {
        [ITEM_STATUS.pending]: "•",
        [ITEM_STATUS.issue]: "!!",
        [ITEM_STATUS.confirmed]: "✓",
        [ITEM_STATUS.exported]: "↗",
        [ITEM_STATUS.discarded]: "–"
      }[status];
    },
    maskCns(cns) {
      if (!cns) return "não informado";
      return `${cns.slice(0, 3)} •••• •••• ${cns.slice(-4)}`;
    },
    formatProcedureCode,
    formatDate(date) {
      return moment(date).format("DD/MM");
    },
    formatDateTime(date) {
      return moment(date).format("DD/MM/YYYY [às] HH:mm");
    }
  }
};
</script>

<style lang="scss" scoped>
$ink: #2b2b3a;
$ink-muted: #6b6b7b;
$primary: #4f3f82;
$primary-soft: #ece9f5;
$grid: #e4e2ea;

$critical: #c0262d;
$critical-soft: #fdecec;
$warning: #9a5b00;
$warning-soft: #fff4e0;
$good: #2e7d32;
$good-soft: #e8f5e9;

.production-page {
  padding-top: 30px;
  padding-bottom: 40px;
  color: $ink;
}

.sp-card {
  padding: 20px 25px;
  border-radius: 15px;
  background-color: #ffffff;
  box-shadow: 2px 4px 7px 0 rgba(0, 0, 0, 0.1);
}

.sp-team {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}

.sp-team-name {
  margin: 0;
  font-size: 22px;
  line-height: 28px;
  font-weight: 700;
  color: $primary;
}

.sp-team-sub {
  font-size: 14px;
  line-height: 20px;
  color: $ink-muted;
}

.sp-mock-badge {
  padding: 4px 12px;
  border: 1px dashed $ink-muted;
  border-radius: 999px;
  font-size: 12px;
  color: $ink-muted;
  white-space: nowrap;
}

.sp-intro {
  max-width: 760px;
  margin-bottom: 28px !important;
  font-size: 14px;
  line-height: 20px;
  color: $ink-muted;
}

.sp-section {
  margin-bottom: 32px;
}

.sp-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}

.sp-header-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.sp-section-title {
  margin-bottom: 12px;
  font-size: 18px;
  font-weight: 700;
  color: $ink;

  .sp-section-header & {
    margin-bottom: 0;
  }
}

.sp-tiles {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.sp-tile {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border-top: 4px solid $primary-soft;

  &.is-critical {
    border-top-color: $critical;

    .sp-tile-value {
      color: $critical;
    }
  }

  &.is-warning {
    border-top-color: $warning;
  }

  &.is-good {
    border-top-color: $good;
  }
}

.sp-tile-value {
  font-size: 32px;
  line-height: 40px;
  font-weight: 700;
}

.sp-tile-of {
  font-size: 16px;
  font-weight: 400;
  color: $ink-muted;
}

.sp-tile-label {
  font-size: 14px;
  line-height: 20px;
}

.sp-tile-note {
  margin-top: 6px !important;
  font-size: 12px;
  line-height: 16px;
  color: $ink-muted;
}

.sp-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.sp-filter {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 1px solid $primary-soft;
  border-radius: 999px;
  background-color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  color: $primary;
  cursor: pointer;

  &.is-active {
    border-color: $primary;
    color: #ffffff;
    background-color: $primary;

    .sp-filter-count {
      color: $primary;
      background-color: #ffffff;
    }
  }
}

.sp-filter-count {
  min-width: 20px;
  padding: 0 6px;
  border-radius: 999px;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
  background-color: $primary-soft;
}

.sp-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 18px;
  border: 1px solid $primary;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;

  &.is-primary {
    color: #ffffff;
    background-color: $primary;
  }

  &.is-ghost {
    color: $primary;
    background-color: #ffffff;
  }

  &.is-small {
    padding: 4px 12px;
    font-size: 12px;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

.sp-table-card {
  overflow-x: auto;
}

.sp-empty {
  font-size: 14px;
  color: $ink-muted;
}

.sp-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th {
    padding: 0 12px 10px 0;
    border-bottom: 1px solid $grid;
    font-size: 12px;
    font-weight: 600;
    text-align: left;
    color: $ink-muted;
  }

  td {
    padding: 12px 12px 12px 0;
    border-bottom: 1px solid $grid;
    vertical-align: top;
  }

  tr:last-child td {
    border-bottom: 0;
  }

  tr.is-discarded td {
    opacity: 0.5;
  }

  .is-nowrap {
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
}

.sp-code {
  display: block;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.sp-strong {
  display: block;
  font-weight: 600;
}

.sp-muted {
  display: block;
  font-size: 12px;
  line-height: 16px;
  color: $ink-muted;
}

.sp-instrument {
  display: inline-block;
  margin-top: 4px;
  padding: 0 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
  color: $primary;
  background-color: $primary-soft;
}

.sp-live {
  display: inline-block;
  margin-top: 4px;
  padding: 0 8px;
  border: 1px solid $good;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  line-height: 16px;
  color: $good;
}

.sp-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  line-height: 20px;
  white-space: nowrap;

  &.is-info {
    color: $primary;
    background-color: $primary-soft;
  }

  &.is-good {
    color: $good;
    background-color: $good-soft;
  }

  &.is-critical {
    color: $critical;
    background-color: $critical-soft;
  }

  &.is-done {
    color: #ffffff;
    background-color: $primary;
  }

  &.is-muted {
    color: $ink-muted;
    background-color: $grid;
  }
}

.sp-issues {
  margin: 6px 0 0 !important;
  padding-left: 16px !important;
  list-style: disc;
  font-size: 12px;
  line-height: 16px;
  color: $critical;
}

.sp-actions {
  white-space: nowrap;

  .sp-button + .sp-button {
    margin-left: 6px;
  }
}

.sp-export {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.sp-export-main {
  flex: 1 1 420px;
}

.sp-export-headline {
  margin-bottom: 6px !important;
  font-size: 15px;
  line-height: 22px;
}

.sp-export-actions {
  display: flex;
  gap: 8px;
}

.sp-preview {
  margin-top: 12px;
  padding: 16px;
  border-radius: 10px;
  overflow-x: auto;
  font-size: 12px;
  line-height: 18px;
  color: $ink;
  background-color: $primary-soft;
  white-space: pre;
}

.sp-history {
  margin-top: 16px;

  ul {
    margin: 0;
    padding: 0 !important;
    list-style: none;
  }

  li {
    padding: 8px 0;
    border-bottom: 1px solid $grid;

    &:last-child {
      border-bottom: 0;
    }
  }
}

.sp-history-title {
  margin-bottom: 6px;
  font-size: 15px;
  font-weight: 700;
}

.sp-reset {
  margin-top: 16px;
  padding: 0;
  border: 0;
  background: transparent;
  font-size: 12px;
  color: $ink-muted;
  text-decoration: underline;
  cursor: pointer;
}

@media (max-width: 1200px) {
  .sp-tiles {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 700px) {
  .sp-tiles {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
